// Anbindung von Tastatur und Maus an die Absichts-Quelle. Klick auf das Canvas fängt die Maus
// (Pointer Lock), Escape gibt sie frei; das macht der Browser selbst und meldet es über
// `pointerlockchange`. Ohne gefangene Maus verarbeitet die Quelle keine Spieltasten.

import type { Intent } from '@bauernhof/shared';
import { createIntentSource } from './intent.js';
import { GAME_KEYS } from './keys.js';

export interface Input {
  /** Die Absicht des Ticks; `interactKey` ist die Taste der verfügbaren Handlung oder undefined. */
  nextIntent(interactKey: string | undefined): Intent;
  /**
   * Ob seit dem letzten Aufruf die Sichttaste (V) gedrückt wurde: Ego-Sicht oder Schulterkamera.
   * Keine Absicht – welche Kamera zeigt, ist reine Ansicht.
   */
  nimmSichtWechsel(): boolean;
  /** Ob seit dem letzten Aufruf T gedrückt wurde: das Satz-Menü öffnen (task-016). Keine Absicht. */
  nimmSatzMenue(): boolean;
  /** Ob Tab gerade gehalten wird: die Anwesenheitsliste zeigen (task-016). */
  listeGehalten(): boolean;
  /** Ob die Maus gerade gefangen ist. */
  isLocked(): boolean;
  /** Fängt die Maus wieder; der Knopf „Weiter spielen“ im Menü tut dasselbe wie ein Klick ins Bild. */
  fangeMaus(): void;
}

export function buildInput(canvas: HTMLCanvasElement): Input {
  const quelle = createIntentSource();
  const istGefangen = (): boolean => document.pointerLockElement === canvas;
  const fangeMaus = (): void => {
    if (istGefangen()) return;
    // Der Browser lehnt den Fang ab, wenn kurz zuvor Escape gedrückt wurde; dann bleibt das Menü
    // stehen und ein weiterer Klick genügt.
    Promise.resolve(canvas.requestPointerLock()).catch(() => undefined);
  };

  canvas.addEventListener('click', fangeMaus);
  document.addEventListener('pointerlockchange', () => quelle.setLocked(istGefangen()));

  window.addEventListener('keydown', (ereignis) => {
    if (!quelle.isLocked()) return;
    if (GAME_KEYS.has(ereignis.code)) ereignis.preventDefault();
    quelle.keyDown(ereignis.code, ereignis.repeat);
  });
  window.addEventListener('keyup', (ereignis) => quelle.keyUp(ereignis.code));
  window.addEventListener('mousemove', (ereignis) => quelle.mouseMove(ereignis.movementX, ereignis.movementY));
  // Verliert das Fenster den Fokus, kommen keine keyup-Ereignisse mehr. Die Freigabe löst
  // `pointerlockchange` aus, und die Quelle vergisst alle gehaltenen Tasten.
  window.addEventListener('blur', () => {
    if (istGefangen()) document.exitPointerLock();
  });

  return {
    nextIntent: (interactKey) => quelle.takeIntent(interactKey),
    nimmSichtWechsel: () => quelle.takeSichtWechsel(),
    nimmSatzMenue: () => quelle.takeSatzMenue(),
    listeGehalten: () => quelle.listeGehalten(),
    isLocked: () => quelle.isLocked(),
    fangeMaus,
  };
}
